<script setup lang="ts">
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useConvexClient } from 'convex-vue';
import { useI18n } from 'vue-i18n';
import { useConvexAuth } from '../composables/useConvexAuth';
import { api } from '../../convex/_generated/api';

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const client = useConvexClient();
const { getAndClearRedirectUrl } = useConvexAuth();

const name = ref('');
const error = ref('');
const isLoading = ref(false);

// Random rotation for the card
const cardRotation = (Math.random() - 0.5) * 2;

const saveName = async () => {
  if (!name.value.trim()) {
    error.value = t('nameSetup.enterNameError');
    return;
  }

  isLoading.value = true;
  error.value = '';

  try {
    await client.mutation(api.users.updateName, { name: name.value.trim() });
    // Preserve invite token when redirecting to invite route
    const inviteToken = route.query.invite as string | undefined;
    if (inviteToken) {
      router.push(`/invite/${encodeURIComponent(inviteToken)}`);
      return;
    }

    const redirectTarget = getAndClearRedirectUrl();
    router.push(redirectTarget || '/workshops');
  } catch (e: any) {
    error.value = e.message || 'Failed to save name';
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="cork-board min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
  >
    <!-- Decorative scattered post-its -->
    <div class="scattered-postits"></div>

    <!-- Additional decorative elements -->
    <div
      class="absolute top-20 left-12 w-20 h-20 bg-(--color-postit-yellow) opacity-10 rotate-12 shadow-lg pointer-events-none"
    ></div>
    <div
      class="absolute bottom-28 right-20 w-16 h-16 bg-(--color-postit-pink) opacity-10 -rotate-15 shadow-lg pointer-events-none"
    ></div>

    <div class="max-w-md w-full relative z-10">
      <!-- Pinned paper card -->
      <div
        class="paper-card cork-pin p-8 rounded-sm"
        :style="{ '--card-rotation': cardRotation + 'deg' }"
      >
        <div class="paper-texture rounded-sm"></div>

        <div class="relative z-10">
          <div class="text-center mb-6">
            <h1 class="text-4xl font-bold text-gray-800 mb-2">{{ $t('nameSetup.welcome') }}</h1>
            <p class="text-gray-600 text-lg">{{ $t('nameSetup.whatToCallYou') }}</p>
          </div>

          <form @submit.prevent="saveName" class="space-y-5">
            <div>
              <label class="block text-gray-700 font-medium mb-2">{{
                $t('nameSetup.yourName')
              }}</label>
              <input
                v-model="name"
                type="text"
                :placeholder="$t('nameSetup.namePlaceholder')"
                class="handwritten-input w-full text-gray-800 text-xl py-3"
                required
                autofocus
              />
            </div>

            <div v-if="error" class="p-3 bg-red-100 border border-red-200 rounded-lg">
              <p class="text-red-700 text-sm">{{ error }}</p>
            </div>

            <button
              type="submit"
              :disabled="isLoading"
              class="push-button w-full px-6 py-4 text-lg rounded-lg"
            >
              {{ isLoading ? $t('common.loading') : $t('common.continue') }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
